import React, { useState, useEffect } from 'react'
import { FaHamburger, FaCheckCircle } from 'react-icons/fa'
import { SimpleTabs, Button, Input, Box, Stack, SimpleTable, Text, Alert } from '@/components/lib'
import api from '../../api'
import utils from '../../utils/utils'

import { nutritionTypes } from '../trials/NewData'

function FoodChooser ({ forId, requiredNutritionFields, value, onComplete }) {
  const [searchAttempted, setSearchAttempted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [amountConsumed, setAmountConsumed] = useState(0)
  const [customName, setCustomName] = useState('')
  const [customNutrition, setCustomNutrition] = useState({})
  const [chosenItem, setChosenItem] = useState()

  useEffect(() => {
    if (!value) {
      setChosenItem(null)
      setAmountConsumed(0)
      setCustomNutrition({})
      setCustomName('')
    }
    if (value?.amountConsumed) {
      setChosenItem(value)
      setAmountConsumed(value.amountConsumed)
      if (value.source === 'manual') {
        setCustomNutrition(value.nutrients || {})
        setCustomName(value.name || '')
      }
    }
  }, [value])

  const search = () => {
    setLoading(true)
    setSearchAttempted(false)
    api.nutrition.search(forId, searchTerm, ({ results }) => {
      setSearchResults(results)
      setLoading(false)
      setSearchAttempted(true)
    }, err => {
      utils.toast('error', err.message)
      setLoading(false)
    })
  }

  const updateFieldNutrition = (resultId, field, value) => {
    const newResults = Object.assign([], searchResults).map(r => {
      if (r.id === resultId) {
        r.nutrients[field.key] = value.replace(/[^\d.]/g, '')
      }
      return r
    })
    setSearchResults(newResults)
  }

  const choose = (selectedFood) => {
    selectedFood.amountConsumed = parseFloat(amountConsumed)
    let error = false;
    (requiredNutritionFields || []).forEach(n => {
      const recorded = selectedFood.nutrients[n]
      if (recorded === null || recorded === undefined || recorded === '') error = true
      selectedFood.nutrients[n] = parseFloat(recorded)
    })
    if (error) return window.alert('Please ensure the required nutritional fields are completed.')
    setChosenItem(selectedFood)
    onComplete(selectedFood)
  }

  const nutritionFields = (requiredNutritionFields || []).map(key => nutritionTypes.find(n => n.key === key))

  return (
    <>
      {chosenItem
        ? (
          <Box bg='bg.accentSubtle' p={3} display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center'>
              <Box as={FaCheckCircle} color='green.500' />
              <img src={chosenItem.photo} style={{ maxHeight: 40, marginLeft: 10 }} />
              <Text ml={3}>{amountConsumed}g of {chosenItem.name}</Text>
            </Box>
            <Button size='sm' onClick={e => setChosenItem(null)}>Choose a different item</Button>
          </Box>)
        : (
          <>
            <SimpleTabs
              tabs={[
                {
                  title: 'Search for food',
                  content: (
                    <>
                      <Box display='flex'>
                        <Input maxWidth={350} size='sm' bg='bg.surface' type='text' placeholder='Enter a barcode number or search term...' onChange={e => setSearchTerm(e.target.value)} value={searchTerm} />
                        <Button onClick={search} isLoading={loading} ml={2} size='sm' variant='outline' colorPalette='primary'><Box as={FaHamburger} mr={2} /> Search food</Button>
                      </Box>

                      {!chosenItem && searchResults?.length > 0 &&
                        <>
                          <Text mt={2} mb={2} fontSize={10} color='fg.default'>Nutritional values are per 100g/ml</Text>
                          <SimpleTable
                            tableProps={{ size: 'sm' }}
                            headers={[]}
                            rows={searchResults.map(result => [
                              <Box key={result.id} display='flex' flexDirection='column' alignItems='center'>
                                <Text mb={2} fontWeight='bold' fontSize={14}>{result.name}</Text>
                                <img style={{ maxHeight: '60px' }} src={result.imageUrl} />
                              </Box>,
                              <Box key={result.id}>
                                {nutritionFields.map(field =>
                                  <Stack direction='row' alignItems='center' key={field.key}>
                                    <Input w='150px' size='sm' placeholder='Per 100g food' value={result.nutrients[field.key]} onChange={e => updateFieldNutrition(result.id, field, e.target.value)} />
                                    <Text>{`${field.unit} ${field.name || ''}`}</Text>
                                  </Stack>
                                )}
                              </Box>,
                              <Box bg='bg.accentSubtle' key={result.id}>
                                <Box display='flex' flexDirection='column' alignItems='center'>
                                  <Text fontSize={10}>Amount consumed</Text>
                                  <Stack direction='row' alignItems='center'>
                                    <Input w='150px' size='sm' bg='bg.surface' placeholder='100' value={amountConsumed} onChange={e => setAmountConsumed(e.target.value.replace(/[^\d]/g, ''))} />
                                    <Text>g or ml</Text>
                                  </Stack>
                                  <Button mt={2} size='sm' onClick={e => choose(result)} colorPalette='green'>Select</Button>
                                </Box>
                              </Box>
                            ])}
                          />
                        </>}
                      {searchAttempted && searchResults && searchResults.length === 0 &&
                        <Alert mt={2} status='warning'>No results were found for your search term. You can add the entries in manually instead.</Alert>}
                    </>)
                },
                {
                  title: 'Enter food manually',
                  content: (
                    <Box>
                      <Text fontSize={12} fontWeight='bold'>Enter a name for the item</Text>
                      <Input bg='bg.surface' placeholder='Food entry name...' value={customName} onChange={e => setCustomName(e.target.value)} />

                      <Text mt={3} fontSize={12} fontWeight='bold'>Enter the required nutritional values per 100g of food</Text>
                      {nutritionFields.map(field =>
                        <Stack key={field.key} direction='row' alignItems='center'>
                          <Input
                            w='150px' size='sm'
                            bg='bg.surface' placeholder='Per 100g food' value={customNutrition[field.key] || ''} onChange={e => {
                              const newCustomValues = Object.assign({}, customNutrition)
                              newCustomValues[field.key] = e.target.value.replace(/[^\d.]/g, '')
                              setCustomNutrition(newCustomValues)
                            }}
                          />
                          <Text>{`${field.unit} ${field.name || ''}`}</Text>
                        </Stack>
                      )}

                      <Text mt={3} fontSize={12} fontWeight='bold'>Enter the number of grams consumed</Text>
                      <Stack direction='row' alignItems='center'>
                        <Input size='sm' w='150px' bg='bg.surface' placeholder='300' value={amountConsumed} onChange={e => setAmountConsumed(e.target.value.replace(/[^\d]/g, ''))} />
                        <Text>g</Text>
                      </Stack>

                      <Button
                        mt={3} colorPalette='green' onClick={e => {
                          choose({
                            source: 'manual',
                            name: customName,
                            nutrients: customNutrition
                          })
                        }}
                      >Add entry
                      </Button>
                    </Box>)
                }
              ]}
            />

          </>)}
    </>
  )
}

export default FoodChooser
